import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL_WITH_API } from '../config/api';
import AdminHeader from '../components/AdminHeader';
import ImpersonationBanner from '../components/ImpersonationBanner';
import SideMenuCard, { SideMenuItem } from '../components/SideMenuCard';
import { isStagingOrLocalhost } from '../utils/environment';
import { clearAllAuthStorage, getEffectiveToken, getEffectiveUser, isImpersonating } from '../utils/authSession';
import { getChromeRuntime } from '../utils/extensionMessaging';
import { getExtensionId } from '../config/extension';
import { MENU_ICON_PATHS } from '../components/menuIcons';

interface AssistPromptResponse {
  source: 'default' | 'db_override' | 'env_override';
  systemPrompt: string;
  userInstructions: string;
  defaultSystemPrompt: string;
  defaultUserInstructions: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

const AdminAiAssistPage = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const usePersistentSidebar = isStagingOrLocalhost();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [source, setSource] = useState<'default' | 'db_override' | 'env_override'>('default');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userInstructions, setUserInstructions] = useState('');
  const [defaultSystemPrompt, setDefaultSystemPrompt] = useState('');
  const [defaultUserInstructions, setDefaultUserInstructions] = useState('');
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'GPS - Traffic Lite Super Admin';
    const viewer = getEffectiveUser();
    if (!viewer?.isSuperAdmin || isImpersonating()) {
      navigate('/admin');
      return;
    }
    void fetchPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignOut = () => {
    if (!window.confirm('Are you sure you want to sign out?')) return;
    clearAllAuthStorage();
    const runtime = getChromeRuntime();
    if (runtime) {
      const message = { action: 'CLEAR_ALL_DATA' };
      const extensionId = getExtensionId();
      try {
        if (extensionId) runtime.sendMessage(extensionId, message, () => {});
        else runtime.sendMessage(message, () => {});
      } catch (sendError) {
        console.error('Sign-out storage clear failed:', sendError);
      }
    }
    window.location.href = `${window.location.origin}${window.location.pathname}#/signout-confirmation`;
  };

  const fetchPrompts = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const token = getEffectiveToken();
      if (!token) {
        setError('Not authenticated. Please sign in again.');
        setLoading(false);
        return;
      }
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      const response = await fetch(`${baseUrl}/admin/assist-prompt`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        cache: 'no-store'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Failed to fetch prompts (${response.status})`);
      }
      const payload = data as AssistPromptResponse;
      setSource(payload.source || 'default');
      setSystemPrompt(payload.systemPrompt || '');
      setUserInstructions(payload.userInstructions || '');
      setDefaultSystemPrompt(payload.defaultSystemPrompt || '');
      setDefaultUserInstructions(payload.defaultUserInstructions || '');
      setUpdatedAt(payload.updatedAt || null);
      setUpdatedBy(payload.updatedBy || null);
    } catch (fetchError: unknown) {
      setError(fetchError instanceof Error ? fetchError.message : 'Failed to fetch GPS prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const token = getEffectiveToken();
      if (!token) {
        setError('Not authenticated. Please sign in again.');
        setSaving(false);
        return;
      }
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      const response = await fetch(`${baseUrl}/admin/assist-prompt`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          systemPrompt,
          userInstructions
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to save GPS prompts');
      setSource('db_override');
      setUpdatedAt(data.updatedAt || null);
      setUpdatedBy(data.updatedBy || null);
      setSuccess('GPS prompt language saved.');
    } catch (saveError: unknown) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to save GPS prompts');
    } finally {
      setSaving(false);
    }
  };

  const sidebarItems: SideMenuItem[] = [
    { key: 'users', label: 'Users', onClick: () => navigate('/admin/users'), icon: MENU_ICON_PATHS.users },
    { key: 'domains', label: 'Domains', onClick: () => navigate('/admin/domains'), icon: MENU_ICON_PATHS.domains },
    { key: 'dashboard', label: 'Data Dashboard', onClick: () => navigate('/admin'), icon: MENU_ICON_PATHS.dataDashboard },
    { key: 'policy', label: 'Policy Templates', onClick: () => navigate('/admin/settings'), icon: MENU_ICON_PATHS.policyDetails },
    { key: 'aiAssist', label: 'GPS', onClick: () => navigate('/admin/ai-assist'), isActive: true, icon: MENU_ICON_PATHS.aiAssist },
    { key: 'feedback', label: 'Feedback', onClick: () => navigate('/admin/feedback'), icon: MENU_ICON_PATHS.feedback },
    { key: 'signout', label: 'Sign Out', onClick: handleSignOut, isDanger: true, icon: MENU_ICON_PATHS.signOut }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className={usePersistentSidebar ? 'max-w-[1600px] mx-auto flex gap-6 items-start' : 'max-w-[1400px] mx-auto'}>
        {usePersistentSidebar && <SideMenuCard title="Super Admin Menu" items={sidebarItems} />}
        <div className={`${usePersistentSidebar ? 'flex-1 min-w-0' : ''} bg-white rounded-lg shadow-md p-8`} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <AdminHeader
            title="Traffic Lite Super Admin"
            subtitle="GPS"
            helperText="Manage Traffic Lite GPS prompt language for staging. Save to apply changes immediately to GPS analyses."
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
          />
          <ImpersonationBanner />

          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading GPS settings...</div>
          ) : (
            <div style={{ display: 'grid', gap: '14px' }}>
              {error && (
                <div style={{ border: '1px solid #fecaca', backgroundColor: '#fef2f2', color: '#b91c1c', borderRadius: '10px', padding: '10px 12px' }}>
                  {error}
                </div>
              )}
              {success && (
                <div style={{ border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', color: '#1d4ed8', borderRadius: '10px', padding: '10px 12px' }}>
                  {success}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div style={{ color: '#334155', fontSize: '13px' }}>
                  <strong>Prompt source:</strong> {source === 'env_override' ? 'Environment fallback' : source === 'db_override' ? 'Saved Super Admin default (active for all users)' : 'Default built-in prompt'}
                  {(updatedAt || updatedBy) && (
                    <span> • Last saved {updatedAt ? new Date(updatedAt).toLocaleString() : '-'}{updatedBy ? ` by ${updatedBy}` : ''}</span>
                  )}
                </div>
                <div style={{ display: 'grid', gap: '8px', justifyItems: 'end' }}>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => void fetchPrompts()}
                      style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#334155', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                    >
                      Refresh
                    </button>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void handleSave()}
                      style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', backgroundColor: '#2563eb', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gap: '12px' }}>
                <label style={{ fontSize: '13px', fontWeight: 700, color: '#1f2937' }}>System prompt</label>
                <textarea
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  rows={11}
                  style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '10px', padding: '10px', fontSize: '13px', lineHeight: 1.45, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}
                />
              </div>

              <div style={{ display: 'grid', gap: '12px' }}>
                <label style={{ fontSize: '13px', fontWeight: 700, color: '#1f2937' }}>User instruction block</label>
                <textarea
                  value={userInstructions}
                  onChange={(e) => setUserInstructions(e.target.value)}
                  rows={14}
                  style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '10px', padding: '10px', fontSize: '13px', lineHeight: 1.45, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}
                />
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAiAssistPage;
